'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { RouteSelectionContextValue } from '@/types/routing';

const RouteSelectionContext = createContext<RouteSelectionContextValue | undefined>(undefined);

const STORAGE_KEY = 'usland-route-selection';

export function RouteSelectionProvider({ children }: { children: React.ReactNode }) {
  const [selectedListings, setSelectedListings] = useState<Set<string>>(new Set());
  const [isMounted, setIsMounted] = useState(false);

  // Load from sessionStorage on mount
  useEffect(() => {
    setIsMounted(true);
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSelectedListings(new Set(parsed));
      }
    } catch (error) {
      console.error('Failed to load route selection from sessionStorage:', error);
    }
  }, []);

  // Save to sessionStorage whenever selection changes
  useEffect(() => {
    if (isMounted) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(selectedListings)));
      } catch (error) {
        console.error('Failed to save route selection to sessionStorage:', error);
      }
    }
  }, [selectedListings, isMounted]);

  const addToListing = useCallback((listingId: string) => {
    setSelectedListings(prev => {
      const newSet = new Set(prev);
      // Limit to 10 properties max
      if (newSet.size >= 10) {
        return prev;
      }
      newSet.add(listingId);
      return newSet;
    });
  }, []);

  const removeFromRoute = useCallback((listingId: string) => {
    setSelectedListings(prev => {
      const newSet = new Set(prev);
      newSet.delete(listingId);
      return newSet;
    });
  }, []);

  const clearRoute = useCallback(() => {
    setSelectedListings(new Set());
  }, []);

  const isRouteEmpty = selectedListings.size === 0;

  const getSelectedCount = useCallback(() => {
    return selectedListings.size;
  }, [selectedListings.size]);

  const value: RouteSelectionContextValue = {
    selectedListings,
    addToListing,
    removeFromRoute,
    clearRoute,
    isRouteEmpty,
    getSelectedCount,
  };

  return (
    <RouteSelectionContext.Provider value={value}>
      {children}
    </RouteSelectionContext.Provider>
  );
}

export function useRouteSelection() {
  const context = useContext(RouteSelectionContext);
  if (context === undefined) {
    throw new Error('useRouteSelection must be used within a RouteSelectionProvider');
  }
  return context;
}
